import { DashboardViewControls } from "@/features/dashboard/components/DashboardViewControls";
import { useDashboardPresentation } from "@/features/dashboard/hooks/useDashboardPresentation";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import {
  SegmentedToggle,
  SurfaceEmptyState,
  SurfaceHeader,
  SurfaceLayout,
  WorkspaceSearchBar,
  cn,
  useReducedMotion,
} from "@ando/ui";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowUpRight,
  BarChart3,
  Building2,
  ChevronDown,
  LayoutDashboard,
  LifeBuoy,
  Lightbulb,
  Pencil,
  Plus,
  ShoppingBag,
  Sparkles,
  Star,
  Users,
  X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { ChartGridPosition } from "@ando/core";

import {
  ReportDateFilter,
  ReportList,
  ReportViewer,
  type ReportSummary,
  filterReports,
  useReport,
  useReportSummaries,
} from "@/features/report";
import { NewReportDialog, VoiceReportsList } from "@/features/voice_report";
import { useSectionGuideStore } from "@/features/onboarding/sectionGuideStore";
import { validateWorkspaceSearch } from "@/features/workspace-router";
import { useHasCapability, useCapability } from "@/features/capabilities";
import {
  AddWidgetWizard,
  CrossFilterBar,
  DashboardEmptyState,
  DashboardGrid,
  DashboardListSidebar,
  DashboardTemplatePicker,
  DashboardToolbar,
  ShareDashboardSheet,
  useDashboardCharts,
  useDashboardMutations,
  useDashboards,
  useReceivedDashboards,
} from "@/features/dashboard";
import {
  EnterpriseDataPanel,
  InlineChartPanel,
  useInlineChartParam,
  useOpenResult,
} from "@/features/structured-data";
import { formatRelativeTime } from "@/features/workspace/hooks/formatRelativeTime";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";

/**
 * Dashboard Hub — saved structured-data charts laid out in a draggable grid.
 *
 * v1 ref: ``ando-frontend/app/pages/DashboardPage.tsx`` (263 LoC). v2 ports
 * the substance (react-grid-layout, edit-mode toggle, layout persistence
 * with 500ms debounce, multi-dashboard switching) and re-skins everything
 * onto the Ando design language (warm-cream surface, ink-scale typography,
 * orange accent for active edit state).
 *
 * Capability gate: ``enterprise_sql`` is the BE-side flag for "user has at
 * least one structured data connection". Without it the dashboard is
 * inherently empty (no SQL → no saved charts), so we show a gated empty
 * state pointing the user to ``/workspace/connectors``. Loading defaults to
 * false (fail-closed) so no flash of "no access" before the manifest lands.
 *
 * Deep linking: ``?dashboard=<id>`` selects a non-default dashboard via the
 * shared ``WorkspaceSearchSchema`` passthrough. Mount auto-selects the
 * first dashboard (matches v1 ``ensureLoaded`` behaviour).
 *
 * Pending V1 parity (May 26, 2026): cross-filter bar, email share dialog,
 * chart-type override, SQL editor, time-range panel, calculated-fields
 * editor, AddChartDialog, clone/delete per-card, KPI card, ECharts wrapper.
 * See ``tasks/dashboard_v1_parity_plan.md`` for the sequence.
 */
export const Route = createFileRoute("/_authed/workspace/dashboard")({
  validateSearch: validateWorkspaceSearch,
  component: AnalysisHub,
});

type AnalysisView = "dashboard" | "report";

/**
 * AnalysisHub — the "Analisi" thematic area. One top tab holding an internal
 * toggle: **Dashboard** (build-your-own data viz) ⇄ **Report** (delivered
 * analytical reports). The toggle lives in the SurfaceHeader `label` slot so
 * it's reachable from every state.
 *
 * Deep linking: `?report=<id>` lands directly on a report (auto-switches the
 * toggle to the Report side), mirroring the dashboard's `?dashboard=<id>`.
 */
function AnalysisHub(): ReactNode {
  const { i18n } = useLingui();
  const search = Route.useSearch();
  const hasReportParam = typeof (search as Record<string, unknown>).report === "string";
  const [view, setView] = useState<AnalysisView>(hasReportParam ? "report" : "dashboard");
  const toggle = (
    <SegmentedToggle<AnalysisView>
      ariaLabel={i18n._(msg`Vista analisi`)}
      value={view}
      onChange={setView}
      options={[
        { id: "dashboard", label: i18n._(msg`Dashboard`), icon: <LayoutDashboard size={12} /> },
        { id: "report", label: i18n._(msg`Report`), icon: <BarChart3 size={12} /> },
      ]}
    />
  );

  if (view === "report") return <ReportHub headerLeading={toggle} />;
  return <DashboardHub headerLeading={toggle} />;
}

/**
 * ReportHub — the "Report" side of Analisi.
 *
 * Two-pane catalog:
 *   1. **Voice of X (AI-generated)** — pipeline-driven reports stored in
 *      ``voice_reports`` / ``voice_report_runs`` (BE-backed). Owns the
 *      "+ Nuovo Voice of X" CTA → ``NewReportDialog``. Cards navigate to
 *      ``/workspace/reports/$reportId`` (dedicated detail surface with
 *      Dataset / Outline / Runs / Settings tabs).
 *   2. **Report importati** — legacy ``ReportList`` (static registry,
 *      hosts the RJ POC). Kept underneath so the imported catalog is
 *      still reachable; deep-link param ``?report=<id>`` still works.
 *
 * The two surfaces intentionally sit side by side rather than behind a
 * sub-toggle: Voice of X is the primary creation flow now, imported is
 * a fallback for external customer payloads.
 */
function ReportHub({ headerLeading }: { headerLeading?: ReactNode }): ReactNode {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const summaries = useReportSummaries();

  // Filter state is ephemeral UI (the catalog is short) — it stays local
  // rather than polluting the shared dashboard route's search schema. Only
  // the open-report id is URL-backed (`?report=`), for deep-linking.
  const [query, setQuery] = useState("");
  const [range, setRange] = useState<{ from: Date | null; to: Date | null }>({
    from: null,
    to: null,
  });
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const filtered = useMemo(
    () => filterReports(summaries, { query, from: range.from, to: range.to }),
    [summaries, query, range],
  );
  const isFiltered = query.trim() !== "" || range.from !== null || range.to !== null;

  const selectedId =
    typeof (search as Record<string, unknown>).report === "string"
      ? ((search as Record<string, unknown>).report as string)
      : null;
  const selected = summaries.find((s) => s.id === selectedId) ?? null;

  const select = useCallback(
    (id: string | null) => {
      void navigate({
        to: "/workspace/dashboard",
        search: (prev) => ({ ...prev, report: id ?? undefined }),
      });
    },
    [navigate],
  );

  if (!selected) {
    return (
      <ReportHubCatalog
        headerLeading={headerLeading}
        navigate={navigate}
        summaries={summaries}
        filtered={filtered}
        query={query}
        setQuery={setQuery}
        range={range}
        setRange={setRange}
        isFiltered={isFiltered}
        select={select}
        newDialogOpen={newDialogOpen}
        setNewDialogOpen={setNewDialogOpen}
      />
    );
  }

  return (
    <SelectedReport summary={selected} headerLeading={headerLeading} onBack={() => select(null)} />
  );
}

// ---------------------------------------------------------------------------
// ReportHub catalog (no selection) — elevated UI/UX pass (Jun 17, 2026).
//
// Rationale (see ``tasks/ui_review_report_hub_jun17.md``):
//   - Voice of X is now a HERO section with gradient surface + embedded CTA,
//     not a sibling of "Report importati". User-research signal: VoX is the
//     primary creation flow; the imported catalog is a frozen POC fallback.
//   - When the user has zero Voice of X reports, we show pedagogical
//     use-case mini-cards (Customer/Employee/Citizen/Reviewer/Support)
//     mapped to the BE template categories — clicking opens the create
//     dialog (template preselection is a TODO until the dialog accepts a
//     ``defaultTemplateSlug`` prop).
//   - "Report importati" demoted to a quiet, collapsed ``<details>`` with
//     a top divider and ``Legacy`` chip, so the imported catalog stays
//     reachable without competing for visual weight.
//   - Framer-motion stagger entrance respects ``useReducedMotion`` —
//     instant render under user opt-out.
// ---------------------------------------------------------------------------

interface VoiceUseCase {
  readonly id: string;
  readonly title: { id: string; message: string };
  readonly description: { id: string; message: string };
  readonly icon: LucideIcon;
}

const VOICE_USE_CASES: readonly VoiceUseCase[] = [
  {
    id: "voice_of_customer",
    title: { id: "vox.uc.customer.title", message: "Voice of Customer" },
    description: {
      id: "vox.uc.customer.desc",
      message: "Sintetizza opinioni, feedback e segnali NPS dei clienti",
    },
    icon: ShoppingBag,
  },
  {
    id: "voice_of_employee",
    title: { id: "vox.uc.employee.title", message: "Voice of Employee" },
    description: {
      id: "vox.uc.employee.desc",
      message: "Analizza engagement, retention e clima interno",
    },
    icon: Users,
  },
  {
    id: "voice_of_citizen",
    title: { id: "vox.uc.citizen.title", message: "Voice of Citizen" },
    description: {
      id: "vox.uc.citizen.desc",
      message: "Misura la voce dei cittadini su servizi e iniziative pubbliche",
    },
    icon: Building2,
  },
  {
    id: "voice_of_reviewer",
    title: { id: "vox.uc.reviewer.title", message: "Voice of Reviewer" },
    description: {
      id: "vox.uc.reviewer.desc",
      message: "Aggrega recensioni e segnali reputazionali multi-canale",
    },
    icon: Star,
  },
  {
    id: "voice_of_support",
    title: { id: "vox.uc.support.title", message: "Voice of Support" },
    description: {
      id: "vox.uc.support.desc",
      message: "Estrae pattern da ticket, conversazioni e knowledge base",
    },
    icon: LifeBuoy,
  },
];

interface ReportHubCatalogProps {
  readonly headerLeading: ReactNode | undefined;
  readonly navigate: ReturnType<typeof useNavigate>;
  readonly summaries: readonly ReportSummary[];
  readonly filtered: readonly ReportSummary[];
  readonly query: string;
  readonly setQuery: (q: string) => void;
  readonly range: { from: Date | null; to: Date | null };
  readonly setRange: (r: { from: Date | null; to: Date | null }) => void;
  readonly isFiltered: boolean;
  readonly select: (id: string | null) => void;
  readonly newDialogOpen: boolean;
  readonly setNewDialogOpen: (open: boolean) => void;
}

function ReportHubCatalog({
  headerLeading,
  navigate,
  summaries,
  filtered,
  query,
  setQuery,
  range,
  setRange,
  isFiltered,
  select,
  newDialogOpen,
  setNewDialogOpen,
}: ReportHubCatalogProps): ReactNode {
  const { i18n } = useLingui();
  const reducedMotion = useReducedMotion();
  // Onboarding tip — persisted across reloads via ``useSectionGuideStore``
  // (same store the per-section guide-boxes use). Key ``voice-x-tip`` lives
  // alongside the other section keys. Dismiss is durable: once collapsed,
  // it stays collapsed until the user restores guidance from Settings →
  // "riattiva aiuti" (which triggers ``restore(audienceKey)``).
  const tipDismissed = useSectionGuideStore(
    (s) => s.collapsed["voice-x-tip"] ?? false,
  );
  const toggleTipDismissed = useSectionGuideStore((s) => s.toggle);
  // Pre-selected template slug — set by the use-case card click so the
  // ``NewReportDialog`` can lock in the matching template without the user
  // having to re-pick. Cleared on close so a subsequent generic "+ Nuovo
  // Voice of X" header CTA opens the dialog empty.
  const [defaultTemplateSlug, setDefaultTemplateSlug] = useState<string | undefined>(
    undefined,
  );

  // Animation primitives — collapse to no-op when the user has opted out of
  // motion. Stagger ladder (60ms per layer, 200ms duration, ease-out) matches
  // the ChatEmptyState convention so the app feels of-a-piece.
  const sectionAnim = (index: number) =>
    reducedMotion
      ? {}
      : {
          initial: { opacity: 0, y: 8 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.2, ease: "easeOut", delay: index * 0.06 },
        };

  const cardAnim = (index: number) =>
    reducedMotion
      ? {}
      : {
          initial: { opacity: 0, y: 6 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.2, ease: "easeOut", delay: 0.18 + index * 0.05 },
        };

  const handleOpenCreate = useCallback(() => {
    setDefaultTemplateSlug(undefined);
    setNewDialogOpen(true);
  }, [setNewDialogOpen]);

  const handleOpenCreateWithTemplate = useCallback(
    (slug: string) => {
      setDefaultTemplateSlug(slug);
      setNewDialogOpen(true);
    },
    [setNewDialogOpen],
  );

  // Clear preselected slug when the dialog closes so a subsequent generic
  // open doesn't inherit the previous use-case selection.
  const handleDialogOpenChange = useCallback(
    (next: boolean) => {
      if (!next) setDefaultTemplateSlug(undefined);
      setNewDialogOpen(next);
    },
    [setNewDialogOpen],
  );

  return (
    <SurfaceLayout
      contentAriaLabel={i18n._(msg`Report`)}
      header={
        <SurfaceHeader
          label={headerLeading}
          title={i18n._(msg`Report`)}
          // The header CTA stays as a secondary affordance — the primary CTA
          // lives INSIDE the hero so it's discoverable without scanning the
          // top-right of the chrome (mobile: top-right is reachable but the
          // in-hero CTA wins on landing).
          actions={
            <button
              type="button"
              onClick={handleOpenCreate}
              aria-label={i18n._(msg`Nuovo Voice of X report`)}
              className="inline-flex min-h-[36px] items-center gap-1.5 rounded-lg border border-ando-orange/40 bg-ando-orange/10 px-3 py-1.5 font-medium text-[12.5px] text-ando-orange transition-colors hover:bg-ando-orange/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/40 focus-visible:ring-offset-2"
            >
              <Sparkles size={14} strokeWidth={1.9} aria-hidden />
              <Trans>Nuovo Voice of X</Trans>
            </button>
          }
        />
      }
    >
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-10 pt-2 pb-12">
        {/* ── Onboarding tip — collapses on dismiss ─────────────────────── */}
        <AnimatePresence initial={false}>
          {!tipDismissed ? (
            <motion.aside
              key="tip-banner"
              role="note"
              aria-label={i18n._(msg`Suggerimento Voice of X`)}
              initial={reducedMotion ? false : { opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -4, height: 0, marginBottom: 0 }}
              transition={{ duration: 0.18, ease: "easeOut" }}
              className="flex items-start gap-3 rounded-xl border border-hairline border-l-2 border-l-ando-orange/55 bg-card/60 px-3.5 py-2.5"
            >
              <span
                aria-hidden
                className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-ando-orange/15 text-ando-orange"
              >
                <Lightbulb size={13} strokeWidth={1.9} />
              </span>
              <p className="min-w-0 flex-1 text-[12.5px] text-ink-2 leading-relaxed">
                <Trans>
                  Ogni Voice of X esegue 7 stage di AI con citation grounding obbligatoria.
                  Apri il dettaglio di un run per leggere il report scientifico passo per passo.
                </Trans>
              </p>
              <button
                type="button"
                onClick={() => toggleTipDismissed("voice-x-tip")}
                aria-label={i18n._(msg`Nascondi suggerimento`)}
                className="-mr-1 -mt-1 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-ink-3 transition-colors hover:bg-black/[0.04] hover:text-ink-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/40"
              >
                <X size={14} strokeWidth={1.9} aria-hidden />
              </button>
            </motion.aside>
          ) : null}
        </AnimatePresence>

        {/* ── Hero: Voice of X (primary) ─────────────────────────────────── */}
        {/* Pencil: no gradient wash, no blur blob — a clean typographic
            header sitting directly on the cream surface. */}
        <motion.section
          {...sectionAnim(0)}
          aria-labelledby="voice-of-x-heading"
          aria-describedby="voice-of-x-subtitle"
          className="flex flex-col gap-5"
        >
          <div className="flex flex-col gap-5">
            <div className="flex flex-wrap items-center gap-2">
              <span
                aria-hidden
                className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-ando-orange/15 text-ando-orange"
              >
                <Sparkles size={15} strokeWidth={1.9} />
              </span>
              <h2
                id="voice-of-x-heading"
                className="font-display font-medium text-[18px] text-ink-1 tracking-tight"
              >
                <Trans>Voice of X</Trans>
              </h2>
              <span className="rounded-full bg-ando-orange/10 px-2 py-0.5 font-medium text-[10px] text-ando-orange uppercase tracking-[0.06em]">
                <Trans>AI-generated</Trans>
              </span>
            </div>
            <p
              id="voice-of-x-subtitle"
              className="max-w-2xl text-[13.5px] text-ink-2 leading-relaxed"
            >
              <Trans>
                Genera report Voice of Customer, Employee, Citizen, Reviewer o Support
                dalla tua knowledge Ando — pipeline AI a 7 stage con citation
                grounding e aggiornamenti on-demand.
              </Trans>
            </p>

            {/* VoiceReportsList renders its own empty state with a CTA when the
                user has zero reports. When reports EXIST it renders the grid.
                We layer use-case cards beneath in both cases as a permanent
                discovery affordance (clicking opens the create dialog). */}
            <VoiceReportsList
              onSelect={(reportId) =>
                void navigate({ to: "/workspace/reports/$reportId", params: { reportId } })
              }
              onCreate={handleOpenCreate}
            />

            {/* Use-case discovery strip — 5 mini cards mapping to the BE
                template categories. Always present (above-the-fold for empty
                state, below the grid when reports exist) so the user sees the
                full surface area of the feature on day one. */}
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between gap-3">
                <h3
                  id="voice-of-x-usecases-heading"
                  className="font-medium text-[11px] text-ink-3 uppercase tracking-[0.08em]"
                >
                  <Trans>Inizia da un caso d'uso</Trans>
                </h3>
                <button
                  type="button"
                  onClick={handleOpenCreate}
                  aria-describedby="voice-of-x-subtitle"
                  className={cn(
                    "inline-flex min-h-[36px] w-full items-center justify-center gap-1.5 rounded-full",
                    "bg-ink-1 px-4 py-2 font-medium text-[13px] text-cream",
                    "transition-colors hover:bg-ink-2",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45 focus-visible:ring-offset-2 focus-visible:ring-offset-cream",
                    "sm:w-auto",
                  )}
                >
                  <Plus size={14} strokeWidth={2} aria-hidden />
                  <Trans>Nuovo Voice of X</Trans>
                </button>
              </div>
              <ul
                aria-labelledby="voice-of-x-usecases-heading"
                className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3"
              >
                {VOICE_USE_CASES.map((uc, idx) => {
                  const Icon = uc.icon;
                  const titleStr = i18n._(uc.title);
                  return (
                    <motion.li key={uc.id} {...cardAnim(idx)} className="list-none">
                      <button
                        type="button"
                        // Forwards the use-case id as ``defaultTemplateSlug``
                        // — mirrors the BE template ``slug`` field 1:1 so the
                        // dialog opens with that template already locked in.
                        // Falls back to a generic open if the BE catalog
                        // hasn't shipped the slug yet (handled in the dialog).
                        onClick={() => handleOpenCreateWithTemplate(uc.id)}
                        aria-label={i18n._(msg`Crea report ${titleStr}`)}
                        className={cn(
                          // Pencil card: translucent warm paper + shadow-as-
                          // hairline (mirrors <PencilCard interactive>), never
                          // a stark white box with a heavy drop shadow.
                          "group flex min-h-[44px] w-full items-start gap-2.5 rounded-2xl bg-white/55 p-3 text-left",
                          "shadow-[0_0_0_1px_rgba(60,40,20,0.06),0_1px_2px_rgba(60,40,20,0.05)]",
                          "outline-none transition-all motion-safe:hover:-translate-y-0.5 hover:bg-white/75",
                          "hover:shadow-[0_0_0_1px_rgba(255,122,26,0.35),0_6px_18px_-6px_rgba(255,122,26,0.24)]",
                          "focus-visible:ring-2 focus-visible:ring-ando-orange/40",
                        )}
                      >
                        <span
                          aria-hidden
                          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-ando-orange/10 text-ando-orange transition-colors group-hover:bg-ando-orange/15"
                        >
                          <Icon size={15} strokeWidth={1.8} />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-medium text-[12.5px] text-ink-1">
                            {titleStr}
                          </span>
                          <span className="mt-0.5 line-clamp-2 block text-[11.5px] text-ink-3 leading-snug">
                            {i18n._(uc.description)}
                          </span>
                        </span>
                        <ArrowUpRight
                          aria-hidden
                          size={14}
                          strokeWidth={1.75}
                          className="mt-0.5 shrink-0 text-ink-4 transition-colors group-hover:text-ando-orange"
                        />
                      </button>
                    </motion.li>
                  );
                })}
              </ul>
            </div>
          </div>
        </motion.section>

        {/* ── Retired: Report importati (legacy static POC catalog). The
            registry is now empty (reports are DB-backed only), so this section
            self-hides for everyone. Kept as an extension point: it reappears
            only if a static entry is ever re-added. ────────────────────────── */}
        {summaries.length > 0 ? (
        <motion.section
          {...sectionAnim(1)}
          aria-labelledby="imported-reports-heading"
          className="border-hairline border-t pt-6"
        >
          <details
            className="group"
            // Auto-open when there is no Voice of X to look at AND the catalog
            // does have at least one row (so the user lands on SOMETHING).
            // We can't read the VoiceReports list status from here (it
            // self-fetches), so the heuristic is "imported has rows" — the
            // expansion is cheap (1 row today) and the user can re-collapse.
            open={summaries.length > 0}
          >
            <summary
              className={cn(
                "flex cursor-pointer list-none flex-wrap items-center justify-between gap-3",
                "rounded-md px-0.5 py-1",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/40",
              )}
            >
              <div className="flex items-center gap-2">
                <ChevronDown
                  aria-hidden
                  size={14}
                  strokeWidth={1.9}
                  className="-rotate-90 text-ink-3 transition-transform group-open:rotate-0 motion-safe:duration-200"
                />
                <h2
                  id="imported-reports-heading"
                  className="font-display font-medium text-[13.5px] text-ink-2"
                >
                  <Trans>Report importati</Trans>
                </h2>
                <span className="rounded-full bg-cream-deep/60 px-2 py-0.5 font-medium text-[10px] text-ink-3 uppercase tracking-[0.06em]">
                  <Trans>Legacy</Trans>
                </span>
              </div>
              <span className="text-[11.5px] text-ink-3 tabular-nums">
                <Trans>{summaries.length} disponibili</Trans>
              </span>
            </summary>
            <div className="mt-4 flex flex-col gap-3">
              <WorkspaceSearchBar
                placeholder={i18n._(msg`Cerca report per nome o azienda…`)}
                ariaLabel={i18n._(msg`Cerca report`)}
                initialQuery={query}
                onSearch={setQuery}
                chipStrip={<ReportDateFilter value={range} onChange={setRange} />}
              />
              <ReportList reports={filtered} onSelect={select} isFiltered={isFiltered} />
            </div>
          </details>
        </motion.section>
        ) : null}
      </div>
      <NewReportDialog
        open={newDialogOpen}
        onOpenChange={handleDialogOpenChange}
        defaultTemplateSlug={defaultTemplateSlug}
      />
    </SurfaceLayout>
  );
}

function SelectedReport({
  summary,
  headerLeading,
  onBack,
}: {
  summary: ReportSummary;
  headerLeading?: ReactNode;
  onBack: () => void;
}): ReactNode {
  const { i18n } = useLingui();
  const { doc, isLoading } = useReport(summary.id);
  return (
    <SurfaceLayout
      contentAriaLabel={i18n._(msg`Report`)}
      header={
        <SurfaceHeader
          label={headerLeading}
          title={summary.title}
          actions={
            <button
              type="button"
              onClick={onBack}
              className="inline-flex items-center gap-1.5 rounded-lg border border-hairline bg-white/60 px-2.5 py-1.5 font-medium text-[12.5px] text-ink-2 transition-colors hover:bg-cream-deep hover:text-ink-1"
            >
              <ArrowLeft size={14} strokeWidth={1.9} />
              <Trans>Tutti i report</Trans>
            </button>
          }
        />
      }
    >
      {isLoading || !doc ? (
        <ReportLoadingSkeleton />
      ) : (
        <ReportViewer doc={doc} embedded reportRef={summary.id} />
      )}
    </SurfaceLayout>
  );
}

function ReportLoadingSkeleton(): ReactNode {
  const { i18n } = useLingui();
  return (
    <output
      className="mx-auto block w-full max-w-6xl animate-pulse pt-2"
      aria-label={i18n._(msg`Caricamento report`)}
    >
      <div className="h-7 w-1/2 rounded bg-hairline/70" />
      <div className="mt-4 flex gap-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-14 w-32 rounded-xl bg-hairline/50" />
        ))}
      </div>
      <div className="mt-8 h-64 rounded-xl bg-hairline/40" />
    </output>
  );
}

function DashboardHub({ headerLeading }: { headerLeading?: ReactNode }): ReactNode {
  const { i18n } = useLingui();
  const hasCapability = useHasCapability("enterprise_sql");
  const { state: capState } = useCapability("enterprise_sql");

  // Jun 17, 2026 — recipients of dashboard shares may have NO enterprise
  // connection of their own (they'll see ``ChartNeedsConnectorState`` per
  // chart card). We still let them into the surface so they can view the
  // shared dashboards. The capability gate now also lets users in when
  // they have ANY received share — the per-card chart state takes over
  // from there.
  const { dashboards: receivedDashboards } = useReceivedDashboards();
  const hasReceivedShares = receivedDashboards.length > 0;
  const hasAccess = hasCapability || hasReceivedShares;

  const search = Route.useSearch();
  const navigate = useNavigate();

  // Deep-link target dashboard id (passthrough of WorkspaceSearchSchema).
  const targetDashboardId =
    typeof (search as Record<string, unknown>).dashboard === "string"
      ? ((search as Record<string, unknown>).dashboard as string)
      : undefined;

  const {
    dashboards,
    isLoading: dashboardsLoading,
    isError: dashboardsError,
    refetch: refetchDashboards,
  } = useDashboards({ enabled: hasAccess });

  // Combined pool for the auto-select effect: owned dashboards first, then
  // received ones. Without this, a recipient with zero owned dashboards
  // landed on ``selectedId === null`` and saw the empty-state instead of
  // their shared dashboards.
  const selectablePool = useMemo(
    () => [
      ...dashboards,
      ...receivedDashboards.map((r) => ({
        id: r.id,
        is_default: r.is_default,
        // Tag so ``useEffect`` below treats received rows the same way.
        __received: true as const,
      })),
    ],
    [dashboards, receivedDashboards],
  );

  // Selected dashboard: deep-link first, then BE default, then first item
  // from the combined pool (owned + received).
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { preferences: viewPreferences, update: updateViewPreferences } = useDashboardPresentation(selectedId);
  useEffect(() => {
    if (selectedId) return;
    if (selectablePool.length === 0) return;
    const targetById = targetDashboardId
      ? selectablePool.find((d) => d.id === targetDashboardId)
      : undefined;
    const def = selectablePool.find((d) => d.is_default);
    const next = targetById ?? def ?? selectablePool[0] ?? null;
    if (next) setSelectedId(next.id);
  }, [selectablePool, selectedId, targetDashboardId]);

  const {
    dashboard,
    isLoading: chartsLoading,
    isError: chartsError,
    refetch: refetchDashboard,
  } = useDashboardCharts(selectedId, { enabled: hasAccess });

  const { saveLayout } = useDashboardMutations();

  // PR-A3 — inline structured-data result panel. URL drives the mount
  // (``?openResult=<msgId>``); when set, the panel renders ABOVE the hub
  // and the existing list+grid below is de-emphasised (opacity-95) so the
  // user still sees the dashboard but the focus stays on the result.
  const { msgId: openResultMsgId, clearOpenResult } = useOpenResult();
  // June 14, 2026 — inline chart panel mounts via ``?inlineChart=<msgId>``.
  // Same precedence as ``openResult`` but the panel chrome is different
  // (no SQL preview, no Save-to-Dashboard CTA — those would be lying about
  // a non-existent query). ``openResult`` wins if both are present (it
  // carries SQL + connection, the richer surface).
  const { msgId: inlineChartMsgId, clearInlineChart } = useInlineChartParam();
  const inlineChartPanelOpen =
    openResultMsgId === null && inlineChartMsgId !== null;
  const panelOpen = openResultMsgId !== null || inlineChartPanelOpen;

  // Jun 17, 2026 — if the recipient opened a SINGLE-CHART share, the
  // ``received`` row carries ``chart_id_filter``: filter the dashboard's
  // chart set down to just that one card. Owners see the full set.
  // The lookup keys on ``id`` (UUID PK) so two received rows for the
  // same dashboard (one full + one chart-filtered) resolve correctly.
  const receivedRow = useMemo(
    () => receivedDashboards.find((r) => r.id === selectedId) ?? null,
    [receivedDashboards, selectedId],
  );
  const chartIdFilter = receivedRow?.chart_id_filter ?? null;
  const allCharts = dashboard?.charts ?? [];
  const charts = useMemo(
    () =>
      chartIdFilter
        ? allCharts.filter((c) => c.id === chartIdFilter)
        : allCharts,
    [allCharts, chartIdFilter],
  );
  const chartCount = charts.length;

  // Map chart-id → title for the cross-filter chip strip. Memoised so a
  // re-render of the route shell doesn't churn the `<CrossFilterBar>` props.
  const chartTitles = useMemo<Readonly<Record<string, string>>>(() => {
    const out: Record<string, string> = {};
    for (const c of charts) out[c.id] = c.title;
    return out;
  }, [charts]);

  // Jun 17, 2026 — caller role on the currently-selected dashboard. The
  // BE returns ``access_role`` on /dashboards/{id} (defaults to ``owner``
  // when omitted, for legacy single-tenant rows). Viewer shares cannot
  // mutate; editor shares can mutate but not share.
  const accessRole = dashboard?.access_role ?? "owner";
  const canEdit = accessRole !== "viewer";
  const canShare = accessRole === "owner";

  const [isEditing, setIsEditing] = useState(false);
  // Belt-and-braces — if the role degrades mid-session (e.g. the owner
  // revoked the editor grant while this tab was open) we collapse out of
  // edit mode so the layout-drag handles disappear immediately. The
  // toolbar will already hide the toggle button next render; this guards
  // the drag/resize affordances inside <DashboardGrid>.
  useEffect(() => {
    if (!canEdit && isEditing) setIsEditing(false);
  }, [canEdit, isEditing]);
  const handleToggleEdit = useCallback(() => {
    if (!canEdit) return;
    setIsEditing((p) => !p);
  }, [canEdit]);

  // PR-3 — AddChartDialog (blank mode opened from the toolbar). State lives
  // in the route because the dialog needs the currently-selected dashboard
  // as a default + must outlive the toolbar's render boundary.
  const [addWidgetOpen, setAddWidgetOpen] = useState(false);
  const handleOpenAddWidget = useCallback(() => setAddWidgetOpen(true), []);

  // May 30, 2026 — ShareDashboardSheet now mounts inline (no longer
  // re-routes to /conversations). The sheet owns the email-share mutation;
  // we only own the open-state because it needs the currently-selected
  // dashboard reference + must survive toolbar re-renders.
  const [shareOpen, setShareOpen] = useState(false);

  const handleRefreshAll = useCallback(() => {
    void refetchDashboards();
    void refetchDashboard();
  }, [refetchDashboards, refetchDashboard]);

  const handleShare = useCallback(() => {
    if (!dashboard) return;
    // BE rejects share grants from non-owners; the toolbar already hides
    // the button, but the route guard keeps the contract honest if a
    // future caller wires this handler from a different surface.
    if (!canShare) return;
    setShareOpen(true);
  }, [canShare, dashboard]);

  const handleAskAndo = useCallback(() => {
    void navigate({ to: "/workspace/conversations", search: {} });
  }, [navigate]);

  const handleLayoutChange = useCallback(
    (positions: ChartGridPosition[]) => {
      if (!selectedId) return;
      saveLayout(selectedId, positions);
    },
    [saveLayout, selectedId],
  );

  // --- Meta strip (chart count + last update) -----------------------------
  const meta = useMemo(() => {
    if (dashboardsLoading && !dashboard) {
      return <span className="text-ink-3"><Trans>Caricamento…</Trans></span>;
    }
    if (!dashboard) return null;
    const updated = formatRelativeTime(dashboard.updated_at);
    return (
      <>
        {/* ``widget`` is invariable in Italian (no plural ``widgets``).
            We promote the count to ``ink-1`` so the number reads as the
            emphasised value against the ``ink-3`` caption rest. */}
        <Trans>
          <strong className="font-medium text-ink-1 tabular-nums">{chartCount}</strong>{" "}
          widget
        </Trans>
        {updated ? (
          <>
            <span aria-hidden> · </span>
            <span><Trans>aggiornata {updated}</Trans></span>
          </>
        ) : null}
        {isEditing ? (
          <>
            <span aria-hidden> · </span>
            <span className="ml-0.5 inline-flex items-center gap-1 rounded-md bg-ando-orange-soft px-1.5 py-0.5 font-medium text-[10px] text-ando-orange uppercase tracking-[0.05em]">
              {/* ``Pencil`` reads as "edit"; ``Lock`` reads as "read-only"
                  (semantic opposite). Swapped May 30, 2026. */}
              <Pencil size={9} strokeWidth={2} />
              <Trans>Modifica attiva</Trans>
            </span>
          </>
        ) : null}
      </>
    );
  }, [chartCount, dashboard, dashboardsLoading, isEditing]);

  // --- Capability loading state — keep the skeleton chrome ---------------
  if (capState === "loading") {
    return (
      <SurfaceLayout
        header={
          <SurfaceHeader
          className="flex-wrap [&>div:last-child]:max-w-full [&>div:last-child]:flex-wrap"
            label={headerLeading ?? i18n._(msg`Dashboard`)}
            title={i18n._(msg`Dashboard`)}
            meta={<span className="text-ink-3"><Trans>Caricamento…</Trans></span>}
          />
        }
      >
        <div />
      </SurfaceLayout>
    );
  }

  // --- Capability gate — no enterprise SQL means no dashboards possible --
  if (!hasAccess) {
    return (
      <SurfaceLayout
        header={
          <SurfaceHeader
            label={headerLeading ?? i18n._(msg`Dashboard`)}
            title={i18n._(msg`Dashboard`)}
          />
        }
      >
        <SurfaceEmptyState
          icon={<BarChart3 size={18} strokeWidth={1.75} />}
          title={i18n._(msg`Collega una fonte dati per iniziare`)}
          description={i18n._(msg`Le dashboard di Ando si compongono da query strutturate sui tuoi database o fogli. Aggiungi un connettore enterprise dal pannello Integrazioni per attivare la funzionalità.`)}
        />
      </SurfaceLayout>
    );
  }

  // ── Focused result mode (May 31, 2026) ──────────────────────────────────
  //
  // When ``?openResult=`` is present, the user navigated here to inspect a
  // single structured-data result — typically auto-routed from the chat
  // composer's "Dati strutturati" focus. Showing the dashboard grid
  // underneath is hostile UX (the result IS the only thing they asked
  // for), so we replace the entire surface with the panel rendered
  // full-bleed. Closing the panel clears the URL param and the user
  // falls back to the regular dashboard hub on the next render.
  if (panelOpen) {
    return (
      <SurfaceLayout
        header={
          <SurfaceHeader
            label={headerLeading ?? i18n._(msg`Dashboard`)}
            title={i18n._(msg`Risultato query`)}
            meta={
              <span className="text-ink-3">
                <Trans>Pinna il grafico per salvarlo nella dashboard.</Trans>
              </span>
            }
          />
        }
      >
        {openResultMsgId !== null ? (
          <EnterpriseDataPanel
            messageId={openResultMsgId}
            onClose={clearOpenResult}
          />
        ) : inlineChartMsgId !== null ? (
          <InlineChartPanel
            messageId={inlineChartMsgId}
            onClose={clearInlineChart}
          />
        ) : null}
      </SurfaceLayout>
    );
  }

  return (
    <SurfaceLayout
      header={
        <SurfaceHeader
          className="flex-col items-stretch lg:flex-row lg:items-end [&>div:first-child]:flex-1 [&>div]:w-full lg:[&>div]:w-auto [&>div:last-child]:flex-wrap [&>div:last-child]:justify-end"
          label={headerLeading ?? i18n._(msg`Dashboard`)}
          title={
            <DashboardTitle
              dashboardName={dashboard?.name}
              dashboards={dashboards}
              onSelect={setSelectedId}
              loading={dashboardsLoading && !dashboard}
            />
          }
          meta={meta}
          actions={
            <DashboardToolbar
              isEditing={isEditing}
              onToggleEdit={handleToggleEdit}
              onRefreshAll={handleRefreshAll}
              onShareDashboard={handleShare}
              onAddWidget={handleOpenAddWidget}
              loading={chartsLoading}
              chartCount={chartCount}
              accessRole={accessRole}
            />
          }
        />
      }
      hero={
        // CompanionHero suppressed (May 31, 2026) — the aggregate
        // briefing was removed (off-topic on Dashboard) and a generic
        // "chiedi ad Ando crea grafico..." chip duplicated the toolbar
        // CTA + the empty-state template picker. Keep the chrome clean
        // and let the dashboard widgets be the focal point.
        null
      }
    >
      <section
        className={cn(
          "-mx-8 flex h-full min-h-[480px] flex-col gap-0 md:flex-row",
        )}
      >
        {/* Left rail — dashboard list + CRUD (PR-1). 260px wide, fixed.
            Owns the "no dashboards" CTA exclusively to avoid two competing
            empty-state CTAs (see DashboardEmptyState gating below). */}
        {viewPreferences.showSidebar || (!dashboardsLoading && dashboards.length === 0) ? <DashboardListSidebar
          selectedId={selectedId}
          onSelect={setSelectedId}
        /> : null}
        <div className="min-h-0 min-w-0 flex-1 overflow-y-auto px-8">
          <div className="mx-auto max-w-7xl">
            <DashboardViewControls value={viewPreferences} onChange={updateViewPreferences} isEditing={isEditing} />
            {/* May 31, 2026 — removed the aggregate ``<BriefingSlot>`` from
                the Dashboard surface. The aggregate brief surfaces inbox /
                calendar / finance digests which are off-topic for a
                dashboard hub: the user came here to look at THEIR widgets,
                not to triage email. Reintroduce ONLY when a
                dashboard-scoped briefing exists (e.g. "Le tue dashboard
                più consultate questa settimana", "3 widget non si
                aggiornano da 7g") — out of scope for now (the BE
                ``/briefing/*`` endpoints have no dashboard domain). */}
            {/* May 30, 2026 — empty/error state coordination. The previous
                revision conflated ``dashboardsError || chartsError`` into a
                single "Impossibile caricare" pane, which fired even when
                the user simply had zero dashboards (the screenshot bug:
                sidebar "Nessuna dashboard" + main "Impossibile caricare").
                We now distinguish 5 distinct states so the user always
                sees exactly one truthful empty/error/loading surface. */}
            {dashboardsError ? (
              // 1) List fetch failed (network/auth). The sidebar can't render
              // either — surface the recovery action in the main pane.
              <SurfaceEmptyState
                icon={<BarChart3 size={18} strokeWidth={1.75} />}
                title={i18n._(msg`Impossibile caricare l'elenco dashboard`)}
                description={i18n._(msg`Probabile errore di rete o sessione. Riprova fra qualche secondo.`)}
                action={
                  <button
                    type="button"
                    onClick={handleRefreshAll}
                    className="hover:-translate-y-px rounded-md bg-ink-2 px-3 py-1.5 text-[12px] text-white"
                  >
                    <Trans>Riprova</Trans>
                  </button>
                }
              />
            ) : dashboardsLoading && dashboards.length === 0 ? (
              // 2) First load — show a skeleton so the chrome doesn't read
              // as "empty" prematurely.
              <DashboardGridSkeleton />
            ) : dashboards.length === 0 ? (
              // 3) No dashboards at all — full-bleed template picker is
              // the primary call-to-action. The sidebar still shows its
              // CTA but the canvas surface here owns the onboarding
              // flow with three intent-clear paths: template, blank, or
              // "ask Ando". May 30, 2026 — SOTA empty state (Linear /
              // Notion pattern, replaces the quiet single-line text).
              <DashboardTemplatePicker
                onStartBlank={handleOpenAddWidget}
                onCreatedFromTemplate={(id) => setSelectedId(id)}
              />
            ) : !selectedId ? (
              // 4) Has dashboards but selection hasn't resolved yet — brief
              // window between mount and the auto-select effect firing.
              <div className="flex h-full items-center justify-center py-16 text-[12.5px] text-ink-3">
                <Trans>Seleziona una dashboard dalla barra laterale.</Trans>
              </div>
            ) : chartsError ? (
              // 5) A dashboard IS selected but its detail call failed —
              // genuine retryable error scoped to this dashboard.
              <SurfaceEmptyState
                icon={<BarChart3 size={18} strokeWidth={1.75} />}
                title={i18n._(msg`Impossibile caricare questa dashboard`)}
                description={i18n._(msg`Riprova fra qualche secondo oppure seleziona un'altra dashboard dalla barra laterale.`)}
                action={
                  <button
                    type="button"
                    onClick={() => void refetchDashboard()}
                    className="hover:-translate-y-px rounded-md bg-ink-2 px-3 py-1.5 text-[12px] text-white"
                  >
                    <Trans>Riprova</Trans>
                  </button>
                }
              />
            ) : chartsLoading && chartCount === 0 ? (
              <DashboardGridSkeleton />
            ) : chartCount === 0 ? (
              <DashboardEmptyState onAskAndo={handleAskAndo} />
            ) : (
              <>
                {/* PR-6 — Cross-filter chip strip. Hidden when zero filters.
                    Sits ABOVE the grid so the active filter set is visible
                    before the user scans the cards below. */}
                <CrossFilterBar chartTitles={chartTitles} />
                <DashboardGrid
                  dashboardId={dashboard?.id ?? selectedId ?? ""}
                  charts={charts}
                  savedLayout={dashboard?.layout}
                  presentation={viewPreferences.presentation}
                  density={viewPreferences.density}
                  isEditing={isEditing}
                  onLayoutChange={handleLayoutChange}
                  accessRole={accessRole}
                />
              </>
            )}
          </div>
        </div>
      </section>
      <AddWidgetWizard
        open={addWidgetOpen}
        onOpenChange={setAddWidgetOpen}
        defaultDashboardId={selectedId ?? undefined}
      />
      {dashboard ? (
        <ShareDashboardSheet
          dashboard={{ id: dashboard.id, name: dashboard.name }}
          open={shareOpen}
          onOpenChange={setShareOpen}
        />
      ) : null}
    </SurfaceLayout>
  );
}

// ---------------------------------------------------------------------------
// SurfaceHeader title slot — supports multi-dashboard picker when >1 exists.
// Single-dashboard tenants just see the name, no chevron.
// ---------------------------------------------------------------------------

interface DashboardTitleProps {
  dashboardName: string | undefined;
  dashboards: ReadonlyArray<{ id: string; name: string; is_default: boolean }>;
  onSelect: (id: string) => void;
  loading: boolean;
}

function DashboardTitle({
  dashboardName,
  dashboards,
  onSelect,
  loading,
}: DashboardTitleProps) {
  const { i18n } = useLingui();
  const [open, setOpen] = useState(false);
  const multi = dashboards.length > 1;

  if (loading) {
    return <span className="text-ink-3"><Trans>Caricamento…</Trans></span>;
  }

  const label = dashboardName ?? i18n._(msg`Dashboard`);

  if (!multi) {
    return <span className="block min-w-0 max-w-full break-words">{label}</span>;
  }

  return (
    <div className="relative flex w-full min-w-0 max-w-full">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="-mx-1.5 -my-0.5 group inline-flex min-w-0 max-w-full items-center gap-1.5 rounded-md px-1.5 py-0.5 text-left transition-colors hover:bg-white/50"
      >
        <span className="min-w-0 truncate" title={label}>{label}</span>
        <ChevronDown
          size={16}
          strokeWidth={1.75}
          className={cn(
            "mt-0.5 shrink-0 text-ink-3 transition-transform",
            open && "rotate-180",
          )}
        />
      </button>
      {open ? (
        <>
          {/* Click-out shim */}
          <div
            className="fixed inset-0 z-30"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          <ul
            className="absolute top-full left-0 z-40 mt-1.5 min-w-[220px] rounded-xl border border-hairline bg-cream p-1 shadow-[0_8px_24px_-12px_rgba(60,40,20,0.25)]"
          >
            {dashboards.map((d) => (
              <li key={d.id}>
                <button
                  type="button"
                  onClick={() => {
                    onSelect(d.id);
                    setOpen(false);
                  }}
                  className="flex w-full items-center justify-between gap-3 rounded-md px-2.5 py-2 text-left font-medium text-[13px] text-ink-1 transition-colors hover:bg-cream-deep"
                >
                  <span className="truncate">{d.name}</span>
                  {d.is_default ? (
                    <span className="shrink-0 rounded bg-ando-orange-soft px-1.5 py-0.5 font-medium text-[9px] text-ando-orange uppercase tracking-[0.05em]">
                      <Trans>Default</Trans>
                    </span>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Skeleton — 4-cell grid placeholder, same vibe as canvases.tsx loading.
// ---------------------------------------------------------------------------

// CLS-safe skeleton — 4-cell grid placeholder shaped like the real
// widget cards (title + axis frame + bars + footer) so the reveal
// doesn't shift layout. May 30, 2026 — replaced plain rectangles to
// match SOTA chart-loading patterns (Linear Insights / Hex skeletons).
function DashboardGridSkeleton() {
  const { i18n } = useLingui();
  return (
    <div
      className="grid grid-cols-1 gap-4 md:grid-cols-2"
      aria-label={i18n._(msg`Caricamento dashboard`)}
      role="status"
    >
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          className="flex h-[320px] animate-pulse flex-col gap-3 overflow-hidden rounded-2xl border border-dashed border-hairline p-4"
        >
          {/* Title bar */}
          <div className="flex items-center justify-between">
            <div className="h-3 w-2/5 rounded bg-hairline" />
            <div className="h-3 w-12 rounded bg-hairline/70" />
          </div>
          {/* Chart body with fake axis frame + bars */}
          <div className="relative flex flex-1 items-end gap-1.5 border-hairline border-b border-l pb-3 pl-3">
            <div
              aria-hidden
              className="absolute inset-y-2 left-0 flex flex-col justify-between"
            >
              {[0, 1, 2, 3].map((y) => (
                <div key={y} className="h-px w-1.5 bg-hairline/60" />
              ))}
            </div>
            {[55, 30, 80, 45, 70, 60, 90, 35].map((h, b) => (
              <div
                key={b}
                className="flex-1 rounded-t bg-hairline/50"
                style={{ height: `${String(h)}%` }}
              />
            ))}
          </div>
          {/* Footer line */}
          <div className="h-2 w-1/3 rounded bg-hairline/50" />
        </div>
      ))}
    </div>
  );
}
